import apiClient from '../config/api';
import { MonthlyReportDTO, ProjectMarginDTO, ExpenseMonthlyReportDTO } from '../types/expense.types';

/**
 * Service de comptabilité et statistiques financières
 */
class AccountingService {
  /**
   * Récupérer le rapport mensuel
   */
  async getMonthlyReport(year: number, month: number): Promise<MonthlyReportDTO> {
    console.log(`📊 Récupération du rapport mensuel pour ${month}/${year}`);
    const response = await apiClient.get<MonthlyReportDTO>(
      `/api/accounting/report/monthly?year=${year}&month=${month}`
    );
    console.log('✅ Rapport mensuel récupéré:', response.data);
    return response.data;
  }

  /**
   * Récupérer le rapport trimestriel
   */
  async getQuarterlyReport(year: number, quarter: number): Promise<MonthlyReportDTO> {
    console.log(`📊 Récupération du rapport trimestriel pour Q${quarter}/${year}`);
    const response = await apiClient.get<MonthlyReportDTO>(
      `/api/accounting/report/quarterly?year=${year}&quarter=${quarter}`
    );
    console.log('✅ Rapport trimestriel récupéré:', response.data);
    return response.data;
  }

  /**
   * Récupérer le rapport annuel
   */
  async getYearlyReport(year: number): Promise<MonthlyReportDTO> {
    console.log(`📊 Récupération du rapport annuel pour ${year}`);
    const response = await apiClient.get<MonthlyReportDTO>(
      `/api/accounting/report/yearly?year=${year}`
    );
    console.log('✅ Rapport annuel récupéré:', response.data);
    return response.data;
  }

  /**
   * Récupérer la marge d'un projet
   */
  async getProjectMargin(projetId: number): Promise<ProjectMarginDTO> {
    console.log(`📊 Récupération de la marge du projet #${projetId}`);
    const response = await apiClient.get<ProjectMarginDTO>(
      `/api/accounting/margin/project/${projetId}`
    );
    console.log('✅ Marge du projet récupérée:', response.data);
    return response.data;
  }

  /**
   * Récupérer les marges de tous les projets
   */
  async getAllProjectMargins(): Promise<ProjectMarginDTO[]> {
    console.log('📊 Récupération des marges de tous les projets');
    const response = await apiClient.get<ProjectMarginDTO[]>('/api/accounting/margin/projects');
    console.log(`✅ ${response.data.length} marge(s) de projet récupérée(s)`);
    return response.data;
  }

  /**
   * Formater un montant en devise
   */
  formatCurrency(amount: number, currency: string = 'EUR'): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
    }).format(amount);
  }

  /**
   * Formater un pourcentage
   */
  formatPercentage(value: number, decimals: number = 2): string {
    return `${value.toFixed(decimals)} %`;
  }

  /**
   * Calculer la couleur d'un indicateur selon sa valeur
   * (vert si positif, rouge si négatif)
   */
  getIndicatorColor(value: number): 'success' | 'error' | 'warning' {
    if (value > 0) return 'success';
    if (value < 0) return 'error';
    return 'warning';
  }

  /**
   * Calculer le trimestre à partir d'un mois
   */
  getQuarterFromMonth(month: number): number {
    return Math.ceil(month / 3);
  }

  /**
   * Obtenir les mois d'un trimestre
   */
  getMonthsInQuarter(quarter: number): number[] {
    const startMonth = (quarter - 1) * 3 + 1;
    return [startMonth, startMonth + 1, startMonth + 2];
  }

  /**
   * Obtenir le label d'un trimestre
   */
  getQuarterLabel(quarter: number): string {
    return `T${quarter}`;
  }

  /**
   * Obtenir le label d'un mois
   */
  getMonthLabel(month: number): string {
    const months = [
      'Janvier',
      'Février',
      'Mars',
      'Avril',
      'Mai',
      'Juin',
      'Juillet',
      'Août',
      'Septembre',
      'Octobre',
      'Novembre',
      'Décembre',
    ];
    return months[month - 1] || '';
  }

  // ========================================
  // Exports Comptables
  // ========================================

  /**
   * Récupérer le rapport mensuel des dépenses
   */
  async getExpenseReport(year: number, month: number): Promise<ExpenseMonthlyReportDTO> {
    console.log(`📊 Récupération du rapport de dépenses pour ${month}/${year}`);
    const response = await apiClient.get<ExpenseMonthlyReportDTO>(
      `/api/accounting/expenses/report?year=${year}&month=${month}`
    );
    console.log('✅ Rapport de dépenses récupéré:', response.data);
    return response.data;
  }

  /**
   * Télécharger le rapport PDF des dépenses
   */
  async downloadExpensePdfReport(year: number, month: number): Promise<void> {
    console.log(`📄 Téléchargement du rapport PDF pour ${month}/${year}`);

    try {
      const response = await apiClient.get(
        `/api/accounting/expenses/export/pdf?year=${year}&month=${month}`,
        {
          responseType: 'blob',
        }
      );

      // Créer un blob et télécharger
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport-depenses-${year}-${String(month).padStart(2, '0')}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      console.log('✅ Rapport PDF téléchargé');
    } catch (error: any) {
      console.error('❌ Erreur lors du téléchargement du PDF:', error);
      throw error;
    }
  }

  /**
   * Télécharger le rapport CSV des dépenses
   */
  async downloadExpenseCsvReport(year: number, month: number): Promise<void> {
    console.log(`📊 Téléchargement du rapport CSV pour ${month}/${year}`);

    try {
      const response = await apiClient.get(
        `/api/accounting/expenses/export/csv?year=${year}&month=${month}`,
        {
          responseType: 'blob',
        }
      );

      // Créer un blob et télécharger
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport-depenses-${year}-${String(month).padStart(2, '0')}.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      console.log('✅ Rapport CSV téléchargé');
    } catch (error: any) {
      console.error('❌ Erreur lors du téléchargement du CSV:', error);
      throw error;
    }
  }

  /**
   * Partager le rapport par email
   */
  async shareExpenseReportByEmail(
    year: number,
    month: number,
    email: string,
    format: 'pdf' | 'csv' = 'pdf'
  ): Promise<void> {
    console.log(`📧 Envoi du rapport ${format.toUpperCase()} à ${email}`);

    try {
      await apiClient.post('/api/accounting/expenses/share', {
        year,
        month,
        email,
        format,
      });

      console.log('✅ Rapport envoyé par email');
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'envoi par email:', error);
      throw error;
    }
  }

  /**
   * Envoyer le rapport complet (ZIP avec CSV + justificatifs) par email
   */
  async sendReportByEmail(
    year: number,
    month: number,
    recipientEmail: string,
    sendCopyToSelf: boolean = true
  ): Promise<void> {
    console.log(`📧 Envoi du rapport ZIP complet pour ${month}/${year} à ${recipientEmail}`);
    console.log(`📧 Copie pour soi-même: ${sendCopyToSelf ? 'Oui' : 'Non'}`);

    try {
      // Formater la période au format YYYY-MM
      const period = `${year}-${String(month).padStart(2, '0')}`;

      await apiClient.post('/api/expenses/send-report', {
        period,
        recipientEmail,
        sendCopyToSelf,
      });

      console.log('✅ Rapport ZIP envoyé par email avec succès');
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'envoi du rapport ZIP par email:', error);
      throw error;
    }
  }
}

export default new AccountingService();
